import React, { useEffect } from 'react';
import {
  Grid, Stack, Typography, Breadcrumbs, Link, Divider, Button, Box, TextField,
  ToggleButtonGroup, ToggleButton, LinearProgress, TableContainer, TablePagination,
  Table, TableHead, TableRow, TableCell, Checkbox, TableBody, Snackbar, Alert
} from '@mui/material';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import AppsIcon from '@mui/icons-material/Apps';
import { useOperational } from 'providers/OperationalContextProvider';
import { useAuth } from 'providers/AuthContextProvider';
import AlertDialog from 'components/common/AlertDialog';
import AppsForm from 'components/forms/AppsForm';

const columns = [
  { id: 'id', label: 'Id', disabled: true, type: 'text', xs: 4, sm: 4, md: 4 },
  { id: 'app_id', label: 'App Id', disabled: true, type: 'text', xs: 4, sm: 4, md: 4 },
  { id: 'tenant_id', label: 'Tenant Id', disabled: true, type: 'text', xs: 4, sm: 4, md: 4 },
  { id: 'name', label: 'Name', disabled: false, xs: 4, sm: 4, md: 4 },
  //{ id: 'description', label: 'description', disabled: false, xs: 4, sm: 4, md: 4 },
  { id: 'platform', label: 'platform', disabled: false, xs: 4, sm: 4, md: 4 },
  { id: 'enabled', label: 'enabled', disabled: false, xs: 4, sm: 4, md: 4 },
  { id: 'created_at', label: 'Created At', disabled: true, type: 'text', xs: 6, sm: 4, md: 4 },
  //{ id: 'updated_at', label: 'Updated At', disabled: true, type: 'date', xs: 6, sm: 4, md: 4 },
];

export default function Apps() {
  const { apps, getApps, postApps, deleteApps } = useOperational();
  const { user } = useAuth();
  
  const [rows, setRows] = React.useState([]);
  const [id, setId] = React.useState(null);
  const [snackbarState, setSnackbarState] = React.useState({ open: false, message: "", status: "info" });
  const [loading, setLoading] = React.useState(true);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openForm, setOpenForm] = React.useState(false);
  const [checked, setChecked] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(3);
  const [lastRow, setLastRow] = React.useState(null);
  const [firstRow, setFirstRow] = React.useState(null);
  const [viewMode, setViewMode] = React.useState('list');
  const [selectedItem, setSelectedItem] = React.useState(null);

  const fetchData = async ()=>{
    try {
      const queryParams = { tenant_id: user.tenant_id}
      await getApps(queryParams);
    } catch (err) {
      setSnackbarState({ open: true, message: err.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (apps) setRows(apps);
  }, [apps]);

  useEffect(() => {
    fetchData();
  }, []);

  const onCloseAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const handleClick = (event, rowId) => {
    setChecked((prevChecked) => {
      const currentIndex = prevChecked.indexOf(rowId);
      const newChecked = [...prevChecked];
      if (currentIndex === -1) {
        newChecked.push(rowId);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      return newChecked;
    });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setChecked(rows.map((n) => n.id));
      return;
    }
    setChecked([]);
  };

  const handleChangePage = async (event, value) => {
    event.preventDefault();
    setLoading(true);
    setPage(value);
    
    const isNext = value > page;
    const queryParams = isNext 
      ? { last_id: lastRow?.id, number: rowsPerPage }
      : { first_id: firstRow?.id, number: rowsPerPage };

    setLoading(true);
    try{
      const response = await getApps(queryParams); 
      setRows(response.data);
      setLastRow(response.data[response.data.length - 1]);
      setFirstRow(response.data[0]);
    } catch (err) {
      setSnackbarState({ open: true, message: err.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const onSubmitSearchName = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const searchName = data.get('name');

    if (!searchName) {
      setSnackbarState({ open: true, message: 'Proporciona el nombre del cliente.', status: 'warning' });
      return;
    }

    setLoading(true);
    try{
      const response = await getApps({ name: searchName }); 
      setRows(response.data);
    } catch (err) {
      setSnackbarState({ open: true, message: err.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const onAlertDialogAccept = async () => {
    try {
      setLoading(true);
      await deleteApps({ id: id });
    } catch (err) {
      setSnackbarState({ open: true, message: err.message, status: "error" });
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  return (
    <React.Fragment>    
      <Grid container spacing={2} columns={12}> 
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Typography variant="h4">Applications</Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">home</Link>
              <Link underline="hover" color="inherit" href="/dashboard/apps">apps</Link>
            </Breadcrumbs>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction={{ xs: 'column', md: 'row' }} 
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />} 
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }} 
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="outlined">Export</Button>
              <Button variant="add" color="primary" onClick={() => { setSelectedItem(null); setOpenForm(true); }}>
                Add
              </Button>
            </Stack>
            <Box component="form" noValidate onSubmit={onSubmitSearchName}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  id="name"
                  name="name"
                  type="text"
                  sx={{ minWidth: 150 }}
                  hiddenLabel
                  variant="outlined"
                  placeholder="Name of the App"
                  inputProps={{ autoComplete: "off" }}
                />
                <Button variant="contained" type="submit">Search</Button>
              </Stack>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, nextView) => nextView && setViewMode(nextView)}
              size="small"
            >
              <ToggleButton value="list" aria-label="list view"><ViewStreamIcon /></ToggleButton>
              <ToggleButton value="grid" aria-label="grid view"><AppsIcon /></ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Grid>
      </Grid>

      {loading ? <LinearProgress /> : <Divider/>}

      <TableContainer>
        <TablePagination
          component="div"
          count={300}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[3, 5]}
        />
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center">
                <Checkbox checked={rows.length > 0 && checked.length === rows.length} onClick={handleSelectAllClick} />
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.id} align="center">
                  <Typography variant="body1" fontWeight="bolder">{column.label}</Typography>
                </TableCell>
              ))}
              <TableCell align="center">
                <Typography variant="body1" fontWeight="bolder">Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row, index) => {
              const isItemSelected = checked.includes(row.id);
              return (
                <TableRow 
                  hover 
                  role="checkbox" 
                  onClick={() => handleClick(null, row.id)}
                  tabIndex={-1} 
                  key={row.id} 
                  selected={isItemSelected}
                  style={{ background: row.color }}
                >
                  <TableCell padding="checkbox" align="center">
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                  {columns.map((column) => {
                    const value = row[column.id];
                    const isDate = column.id === "created_at" || column.id === "updated_at";     
                    return (
                      <TableCell key={column.id} align={column.align || 'center'}>
                        {isDate && value?._seconds ? (
                          <Typography variant="body2" color="gray" align="center">
                            {new Date(value._seconds * 1000).toLocaleString('en-GB', { timeZone: 'UTC' })}
                          </Typography>
                        ) : column.id === "enabled" ? (
                          <Typography variant="body2" color={value ? "green" : "red"} align="center">
                            {value ? "Activo" : "Inactivo"}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="gray" align="center">{value ? String(value) : ''}</Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Button color="secondary" onClick={(e) => { e.stopPropagation(); setSelectedItem(row); setOpenForm(true); }}>Ver</Button>
                    <Button color="warning" onClick={(e) => { e.stopPropagation(); setId(row.id); setOpenDialog(true); }} >Eliminar</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AlertDialog 
        title="¿Deseas eliminar este item?" 
        message="Al presionar aceptar los cambios son permanentes." 
        open={openDialog} 
        setOpen={setOpenDialog} 
        onAccept={onAlertDialogAccept} 
      />

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} open={snackbarState.open} onClose={onCloseAlert} autoHideDuration={3000}>
        <Alert onClose={onCloseAlert} severity={snackbarState.status} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>

      <AppsForm 
        key={selectedItem ? selectedItem.id : 'new-client'}
        open={openForm} 
        onClose={()=>{
          setOpenForm(false);
          setSelectedItem(null);
          fetchData()
        }} 
        data={selectedItem}
        onOrderUpdated={() => {
          setOpenForm(false)
          fetchData()
        }}
      />
    </React.Fragment>
  );
}